import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBarcode } from '@fortawesome/pro-light-svg-icons';
import { useToast } from '@shared/hooks/use-toast';

interface GroupScannerProps {
  orderLines?: any[];
  orderId?: number | null;
  onStartLine?: (lineId: number) => Promise<void>;
  onGroupValidated?: (groupCode: string, groupData: any) => void;
}

/**
 * Scanner de grupo - Componente para escanear códigos de grupo
 *
 * Permite escanear o escribir manualmente el código de un grupo
 * para validarlo y continuar con el proceso de trabajo
 */
export const GroupScanner = ({ orderLines = [], orderId, onStartLine, onGroupValidated }: GroupScannerProps) => {
  const { t } = useTranslation('extraWorkOrdersModule');
  const [groupCode, setGroupCode] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  // Auto-focus al montar el componente
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleValidate = async () => {
    if (!groupCode.trim() || isValidating) {
      console.log('🔍 GroupScanner - Validación cancelada:', { groupCode: groupCode.trim(), isValidating });
      return;
    }

    console.log('🔍 GroupScanner - Iniciando validación...');
    setIsValidating(true);
    try {
      console.log('🔍 GroupScanner - Buscando grupo:', {
        groupCode: groupCode.trim(),
        orderLinesCount: orderLines.length,
        orderLines: orderLines.map((l) => ({ id: l.id, group_code: l.group_code })),
      });

      // Buscar grupo en las líneas de la orden actual
      const foundGroup = orderLines.find((line) => line.group_code?.toLowerCase() === groupCode.trim().toLowerCase());

      console.log('🔍 GroupScanner - Resultado de búsqueda:', foundGroup ? 'ENCONTRADO' : 'NO ENCONTRADO', foundGroup);

      if (foundGroup) {
        console.log('✅ GroupScanner - Grupo encontrado:', foundGroup);

        // Validar que el grupo no esté completado
        if (foundGroup.status === 'DONE') {
          console.warn('⚠️ GroupScanner - Grupo ya completado:', foundGroup.group_code);
          toast({
            title: t('detail.scanner.success.group_completed_title', 'Grupo completo!'),
            description: t('detail.scanner.success.group_completed_message', 'No hay nada que hacer'),
            variant: 'default',
          });

          // Limpiar input y reenfocar
          setGroupCode('');
          setTimeout(() => {
            inputRef.current?.focus();
          }, 100);
          return;
        }

        // Si el grupo está en estado PENDING, iniciarlo automáticamente
        if (foundGroup.status === 'PENDING' && orderId && onStartLine) {
          try {
            console.log('🔍 GroupScanner - Iniciando línea automáticamente:', foundGroup.id);
            await onStartLine(foundGroup.id);

            // Actualizar el estado del grupo después de iniciarlo
            foundGroup.status = 'IN_PROGRESS';
            foundGroup.started_at = new Date().toISOString();

            // Mostrar toast de éxito al iniciar
            toast({
              title: t('detail.scanner.success.title', 'Éxito'),
              description: t('detail.scanner.success.group_started', 'Grupo iniciado correctamente'),
              variant: 'default',
            });
          } catch (error: any) {
            console.error('❌ GroupScanner - Error al iniciar línea:', error);

            // Mostrar toast con el error específico de la API
            const errorMessage =
              error?.response?.data?.message ||
              error?.message ||
              t('detail.scanner.error.start_line_failed', 'Error al iniciar la línea de trabajo');

            toast({
              title: t('detail.scanner.error.title', 'Error'),
              description: errorMessage,
              variant: 'destructive',
            });

            // No continuar si falla el inicio
            setGroupCode('');
            setTimeout(() => {
              inputRef.current?.focus();
            }, 100);
            return;
          }
        } else {
          // Si el grupo ya estaba iniciado o no necesita iniciarse, mostrar toast de éxito
          toast({
            title: t('detail.scanner.success.title', 'Éxito'),
            description: t('detail.scanner.success.group_validated', 'Grupo validado correctamente'),
            variant: 'default',
          });
        }

        // Llamar callback con el código y datos del grupo
        onGroupValidated?.(groupCode.trim(), foundGroup);

        // Limpiar input después de validar
        setGroupCode('');

        // Reenfocar para el siguiente escaneo
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      } else {
        // No se encontró el grupo
        toast({
          title: t('detail.scanner.error.title', 'Error'),
          description: t('detail.scanner.error.group_not_found', 'Grupo no encontrado'),
          variant: 'destructive',
        });

        // Limpiar input y reenfocar
        setGroupCode('');
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
    } catch (error: any) {
      console.error('❌ GroupScanner - Error al buscar grupo:', error);

      // Mostrar toast de error
      toast({
        title: t('detail.scanner.error.title', 'Error'),
        description:
          error?.response?.data?.message ||
          error?.message ||
          t('detail.scanner.error.unexpected', 'Error inesperado al buscar el grupo'),
        variant: 'destructive',
      });

      // Limpiar input y reenfocar
      setGroupCode('');
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    } finally {
      setIsValidating(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    console.log('🔍 GroupScanner - Tecla presionada:', e.key);
    if (e.key === 'Enter' || e.keyCode === 13) {
      e.preventDefault();
      e.stopPropagation();
      console.log('🔍 GroupScanner - Enter presionado, validando grupo...', { groupCode, isValidating });
      // Llamar directamente sin esperar
      handleValidate();
    }
  };

  return (
    <div className="bg-card-bg border border-card-border rounded-lg p-6 space-y-4">
      {/* Título - Centrado */}
      <h2 className="text-xl font-bold text-title text-center">
        {t('detail.scanner.group_title', 'Escáner de Grupo')}
      </h2>

      {/* Instrucciones - Centrado */}
      <p className="text-sm text-complementary-text text-center !mt-1">
        {t('detail.scanner.group_instructions', 'Escanea el código del grupo para continuar')}
      </p>

      {/* Input de escaneo */}
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-icons">
          <FontAwesomeIcon icon={faBarcode} className="w-4 h-4" />
        </span>
        <Input
          ref={inputRef}
          placeholder={t('detail.scanner.group_placeholder', 'Escanea o escribe el código del grupo...')}
          value={groupCode}
          onChange={(e) => setGroupCode(e.target.value)}
          onKeyDown={handleKeyDown}
          type="text"
          disabled={isValidating}
          className="w-full pl-10 text-center"
          data-testid="group-scanner-input"
        />
      </div>

      {/* Botón de validar - Centrado */}
      <Button
        variant="primary"
        size="lg"
        onClick={handleValidate}
        disabled={!groupCode.trim() || isValidating}
        className="w-full"
        data-testid="validate-group-button"
      >
        {isValidating
          ? t('detail.scanner.validating', 'Validando...')
          : t('detail.scanner.validate_group', 'Validar Grupo')}
      </Button>
    </div>
  );
};
